import React, { useState, useEffect } from 'react';
import { Box, Paper, TextField, Typography, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Divider, Tooltip } from '@mui/material';
import { API_BASE_URL } from '../config';
import { apiGet } from '../utils/api';
import { authaltFetch, AUTHALT_API_BASE } from '../services/authaltApi';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;

const Billing = () => {
  const [guests, setGuests] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestQuery, setGuestQuery] = useState('');
  const [billingData, setBillingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    subtotal: 0,
    taxTotal: 0,
    grandTotal: 0,
    paidTotal: 0,
    balanceDue: 0,
    totalServices: 0,
    totalDiscounts: 0  // Total savings from discounts/complimentary
  });

  // Load guests for autocomplete
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        // Fetch all guest types using the new API endpoints with POST
        const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
        let allGuestsData = [];
        
        for (const type of types) {
          try {
            const res = await authaltFetch('/guestlistdatav3', {
              type,
              index: 'atmantan'
            });
            if (res.ok) {
              const response = await res.json();
              const categoryKey = type === 'InHouse' ? 'inHouse' :
                                 type === 'TodaysArrival' ? 'todaysArrival' :
                                 type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
              const guestsFromType = response[categoryKey]?.data || [];
              allGuestsData = allGuestsData.concat(guestsFromType);
            }
          } catch (e) {
            console.error(`Failed to fetch ${type}:`, e);
          }
        }
        
        const today = new Date();
        const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const parseDate = (s) => {
          const v = (s || '').trim();
          if (!v) return null;
          const fmts = [
            /^(\d{4})-(\d{2})-(\d{2})$/, // YYYY-MM-DD
            /^(\d{2})-(\d{2})-(\d{4})$/, // DD-MM-YYYY
            /^(\d{2})\/(\d{2})\/(\d{4})$/ // DD/MM/YYYY
          ];
          for (const f of fmts) {
            const m = v.match(f);
            if (m) {
              if (f === fmts[0]) return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
              if (f === fmts[1]) return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
              if (f === fmts[2]) return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
            }
          }
          const d = new Date(v);
          return isNaN(d.getTime()) ? null : d;
        };
        const todayStart = startOfDay(today);
        const list = (Array.isArray(allGuestsData) ? allGuestsData : []).map((g) => ({
          id: g.id || g.guestId || g.GuestId || '',
          name: g.name || g.guestName || g.firstName && g.lastName ? `${g.firstName} ${g.lastName}` : g.firstName || '',
          room: g.room || g.roomNo || g.location || '',
          checkIn: g.checkIn || g.startDate || g.arrivalDate || '',
          checkOut: g.checkOut || g.endDate || g.departureDate || '',
        }));
        const filtered = list.filter((g) => {
          const co = parseDate(g.checkOut);
          return !co || startOfDay(co) >= todayStart;
        });
        const sorted = filtered.filter((g) => (g.id || g.name)).sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setGuests(sorted);
      } catch (e) {
        setGuests([]);
      }
    };
    fetchGuests();
  }, []);

  // Fetch billing data when guest is selected
  useEffect(() => {
    if (!selectedGuest) {
      setBillingData([]);
      return;
    }

    const fetchBillingData = async () => {
      try {
        setLoading(true);
        const response = await apiGet(`/api/billing/guest/${selectedGuest.id}/summary`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch billing data');
        }
        
        const data = await response.json();
        
        // Set billing data from API
        const services = data.services || [];
        setBillingData(services);
        
        // Calculate total discounts from services
        const totalDiscounts = services.reduce((sum, item) => {
          const discountAmount = parseFloat(item.upgrade_discount_amount || item.standalone_discount_amount || 0);
          return sum + discountAmount;
        }, 0);
        
        // Set summary from API
        setSummary({
          subtotal: parseFloat(data.subtotal || 0),
          taxTotal: parseFloat(data.tax_total || 0),
          grandTotal: parseFloat(data.grand_total || 0),
          paidTotal: parseFloat(data.paid_total || 0),
          balanceDue: parseFloat(data.balance_due || 0),
          totalServices: data.total_services || 0,
          totalDiscounts: totalDiscounts
        });
      } catch (error) {
        console.error('Error fetching billing data:', error);
        setBillingData([]);
        setSummary({
          subtotal: 0,
          taxTotal: 0,
          grandTotal: 0,
          paidTotal: 0,
          balanceDue: 0,
          totalServices: 0,
          totalDiscounts: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, [selectedGuest]);

  const getStatusChip = (status) => {
    const statusConfig = {
      'pending': { label: 'Pending', color: 'warning' },
      'paid': { label: 'Paid ✓', color: 'success' },
      'waived': { label: 'Waived', color: 'default' },
      'cancelled': { label: 'Cancelled', color: 'error' }
    };
    const config = statusConfig[status] || statusConfig['pending'];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString('en-IN') || 0}`;
  };

  const getTypeDisplay = (item) => {
    if (item.is_package_service) return 'Package';
    if (item.is_upgrade) {
      return `Upgrade (${item.upgrade_status})`;
    }
    return 'Standalone';
  };

  const getDiscountInfo = (item) => {
    const isUpgradeDiscount = item.is_upgrade && 
      (item.upgrade_status === 'complimentary' || item.upgrade_status === 'discounted');
    const isStandaloneDiscount = item.is_standalone && 
      (item.standalone_billing_status === 'complimentary' || item.standalone_billing_status === 'discounted');
    
    if (!isUpgradeDiscount && !isStandaloneDiscount) return null;
    
    const status = item.upgrade_status || item.standalone_billing_status;
    const discountAmount = parseFloat(item.upgrade_discount_amount || item.standalone_discount_amount || 0);
    const reason = item.upgrade_reason || item.standalone_reason || '';
    const originalAmount = parseFloat(item.billable_amount) + discountAmount;
    
    return {
      type: status,
      amount: discountAmount,
      reason: reason,
      originalAmount: originalAmount,
      isComplimentary: status === 'complimentary'
    };
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', p: 2, gap: 2, bgcolor: '#f6f6f6' }}>
      {/* Left Panel - Guest Search */}
      <Paper sx={{ width: 360, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Billing</Typography>
        
        <Autocomplete
          options={guests}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.name || option.id || '';
          }}
          value={selectedGuest}
          onChange={(e, newValue) => {
            setSelectedGuest(newValue);
            if (newValue) {
              setGuestQuery(newValue.name || newValue.id || '');
            } else {
              setGuestQuery('');
            }
          }}
          onInputChange={(e, newInputValue) => {
            if (e && e.type === 'change') {
              setGuestQuery(newInputValue);
            }
          }}
          freeSolo
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Search Guest"
              placeholder="Search by name or ID"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id || option.name}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {option.id}{option.room ? ` • Room ${option.room}` : ''}
                </Typography>
              </Box>
            </li>
          )}
        />

        {selectedGuest && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Guest Details</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Name:</strong> {selectedGuest.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>ID:</strong> {selectedGuest.id}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Room:</strong> {selectedGuest.room || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Check-in:</strong> {selectedGuest.checkIn || 'N/A'}
              </Typography>
              <Typography variant="body2">
                <strong>Check-out:</strong> {selectedGuest.checkOut || 'N/A'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Bill Summary</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2">Total Services:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{summary.totalServices}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(summary.subtotal)}</Typography>
              </Box>
              {summary.totalDiscounts > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, bgcolor: '#e8f5e9', px: 1, borderRadius: 1, my: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                    Total Savings:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                    −{formatCurrency(summary.totalDiscounts)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2">Tax:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(summary.taxTotal)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Grand Total:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#E4C984' }}>{formatCurrency(summary.grandTotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'success.main' }}>Paid:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>{formatCurrency(summary.paidTotal)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, bgcolor: summary.balanceDue > 0 ? '#fff3e0' : 'transparent', px: 1, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>Balance Due:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: summary.balanceDue > 0 ? 'error.main' : 'success.main' }}>
                  {formatCurrency(summary.balanceDue)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Right Panel - Billing Details */}
      <Paper sx={{ flex: 1, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {selectedGuest ? `Bill Details - ${selectedGuest.name}` : 'Select a guest to view billing'}
        </Typography>

        {loading && <Typography>Loading...</Typography>}

        {!loading && selectedGuest && billingData.length === 0 && (
          <Typography variant="body2" color="text.secondary">No billing records found for this guest.</Typography>
        )}

        {!loading && selectedGuest && billingData.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Tax</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingData.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{new Date(item.service_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.treatment_name}</Typography>
                          {item.is_upgrade && item.original_treatment_name && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              ↳ From {item.original_treatment_name}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Discount Badge */}
                        {(() => {
                          const discount = getDiscountInfo(item);
                          if (!discount) return null;
                          
                          return (
                            <Tooltip 
                              title={
                                <Box sx={{ p: 0.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    {discount.isComplimentary ? 'Complimentary Service' : 'Discounted Service'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    Original Amount: {formatCurrency(discount.originalAmount)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', color: 'success.light' }}>
                                    Discount: −{formatCurrency(discount.amount)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                    Final Amount: {formatCurrency(item.billable_amount)}
                                  </Typography>
                                  {discount.reason && (
                                    <>
                                      <Divider sx={{ my: 0.5, bgcolor: 'rgba(255,255,255,0.2)' }} />
                                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        Reason: {discount.reason}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              }
                              placement="right"
                              arrow
                            >
                              <Chip 
                                size="small"
                                label={discount.isComplimentary ? 'FREE' : 'DISC'}
                                color={discount.isComplimentary ? 'success' : 'info'}
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  ml: 0.5
                                }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{getTypeDisplay(item)}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(item.billable_amount)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.tax_amount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.total_amount)}</TableCell>
                    <TableCell>{getStatusChip(item.payment_status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!selectedGuest && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
            <Typography variant="body1">Please select a guest from the left panel to view their billing details</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Billing;
